import { Segment, Segments } from "@/types/types.ts"
import React, { useCallback, useEffect, useMemo } from "react"
import { FlexBox } from "@/common/components/Box/FlexBox.tsx";
import clsx from "clsx";
import {
  DataGridPro, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridColDef, GridRowParams, useGridApiRef
} from "@mui/x-data-grid-pro";
import styles from '@/features/AudioWorkstation/components/AudioEditorTabs/DetailsPane.module.scss';
import {
  SegmentTableRow, SegmentTableRowProps
} from "@/features/AudioWorkstation/components/AudioEditorTabs/EditorTabPane/SegmentTableRow.tsx";
import useUniqueTabId from "@/common/hooks/useUniqueTabId.ts";
import { useAudioPlayback } from "@/common/hooks/useAudioPlayback.ts";
import _ from "lodash";
import { useMutateExplorerItems } from "@/state/jotai/hooks/useMutateExplorerItems.ts";
import useCurrentExplorerItem from "@/common/hooks/useCurrentExplorerItem.ts";
import { useCellEditMode } from "@/common/hooks/useCellEditMode.ts";
import { updateSegment } from "@/services/api/track.ts";
import {
  NoSegmentRowsOverlay
} from "@/features/AudioWorkstation/components/AudioEditorTabs/EditorTabPane/NoSegmentRowsOverlay.tsx";
import useCurrentExplorerItemId from "@/common/hooks/useCurrentExplorerItemId.ts";
import {
  EditorPanelColumns
} from "@/features/AudioWorkstation/components/AudioEditorTabs/EditorTabPane/EditorPanelColumns.tsx";
import { useMediaQuery } from "@mui/material";
import useUserIsWriting from "@/common/hooks/useUserIsWriting.ts";
import { Theme } from "@mui/material/styles";
import useSegmentsDataGridApi from "@/common/hooks/useSegmentsDataGridApi.ts";

const EditorPanel: React.FC = () => {
  const apiRef = useGridApiRef();
  const currentExplorerItem = useCurrentExplorerItem();
  const currentTrack = currentExplorerItem?.track;
  const [currentExplorerItemId] = useCurrentExplorerItemId()
  const segments = currentTrack?.segments;
  const isMediumScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const reorderSegments = (segments: Segments) => _.sortBy(segments, ["startTime"]);
  const rows = useMemo(() => segments ? Object.values(reorderSegments(segments)) : [], [segments]);
  const userIsWriting = useUserIsWriting()
  const [uniqueTabId] = useUniqueTabId();
  const [, setSegmentsDataGridApi] = useSegmentsDataGridApi();
  const { play, seek } = useAudioPlayback();
  const {
    cellModesModel,
    handleCellClick,
    handleCellEditStart,
    handleCellEditStop,
  } = useCellEditMode()
  const {
    updateTrackSegment,
  } = useMutateExplorerItems();

  useEffect(() => {
    if (!apiRef.current) {
      return;
    }

    setSegmentsDataGridApi(apiRef);
  }, [
    apiRef,
    setSegmentsDataGridApi
  ]);

  const handleProcessRowUpdate = useCallback((updatedSegment: Segment, originalSegment: Segment) => {
    if (!currentExplorerItemId) {
      console.log('Warning: currentExplorerItemId is not defined')
      return originalSegment;
    }

    updateTrackSegment(currentExplorerItemId, updatedSegment)

    updateSegment(
      currentExplorerItemId,
      updatedSegment,
      uniqueTabId,
    );

    return updatedSegment
  }, [
    updateTrackSegment,
    currentExplorerItemId,
    uniqueTabId,
  ]);

  /*const autosizeOptions = {
    columns: ['labelText'],
    outliersFactor: 1,
    expand: DEFAULT_GRID_AUTOSIZE_OPTIONS.expand,
    includeHeaders: DEFAULT_GRID_AUTOSIZE_OPTIONS.includeHeaders,
    includeOutliers: DEFAULT_GRID_AUTOSIZE_OPTIONS.includeOutliers,
  };*/

  const hiddenFields = [
    'id',
    'labelText',
    'playBack',
    'actions',
  ];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  const replaySegment = useCallback((segment: Segment) => {
    if (!currentExplorerItem) {
      console.log('Warning: currentExplorerItem is not defined @ SegmentTableRow.replaySegment')
      return
    }

    seek(segment.startTime)
    play()
  }, [
    currentExplorerItem,
    play,
    seek
  ])
  return (
    <>
      <DataGridPro
        sx={{
          display: "flex",
          flexGrow: 1,
          minHeight: 0,
          maxHeight: '100%',
          px: 2,
        }}
        disableRowSelectionOnClick={true}
        loading={false}
        apiRef={apiRef}
        density={'compact'}
        columns={EditorPanelColumns()}
        rows={rows}
        keepColumnPositionIfDraggedOutside={true}
        onRowDoubleClick={(params: GridRowParams) => {
          if (userIsWriting()) return;

          replaySegment(params.row)
        }}
        cellModesModel={cellModesModel}
        onCellClick={handleCellClick}
        onCellEditStart={handleCellEditStart}
        onCellEditStop={handleCellEditStop}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Error updating row:', error)}
        getDetailPanelHeight={() => 'auto'}
        // autosizeOptions={autosizeOptions}
        slots={{
          noRowsOverlay: () => {
            return <NoSegmentRowsOverlay />
          },
          row: (props) => {
            const selectedRows = apiRef.current?.getSelectedRows?.() || new Map();

            return (
              <FlexBox className={clsx({
                [styles.segmentRowIsSelected]: selectedRows.has(props.rowId || ''),
              })}>
                <SegmentTableRow {...props as SegmentTableRowProps} />
              </FlexBox>
            );
          }
          // toolbar: ExplorerToolbar
        }}
        slotProps={{
          columnsManagement: {
            getTogglableColumns,
          },
          columnsPanel: {
            sx: {
              /* "& .MuiDataGrid-columnsManagementRow:nth-of-type(1)": { // hides detail panel toggle
                 display: "none"
               },*/
            }
          }
        }}
        localeText={{
          toolbarColumns: isMediumScreen ? '' : 'Columns',
        }}
        initialState={{
          columns: {
            columnVisibilityModel: {
              createdAt: false,
              updatedAt: false,
              startTime: false,
              endTime: false,
              [GRID_DETAIL_PANEL_TOGGLE_FIELD]: false,
            },
          },
        }}
      />
    </>
  )
}

export { EditorPanel }
